import React, { useEffect, useState } from "react";
import { api, formatRupiah, STAGE_META, QUAL_META, stageLabel, stageStyle } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, GitBranch, Search, Upload, Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ImportDialog from "../components/ImportDialog";
import { downloadCsv } from "../lib/download";

const empty = { name:"", company:"", contact_person:"", phone:"", whatsapp:"", email:"", lead_source:"", industry:"", status:"new", qualification:"warm", potential_value:0, product:"", notes:"" };

export default function Leads() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [stages, setStages] = useState([]);
  const [sources, setSources] = useState([]);
  const [industries, setIndustries] = useState([]);

  const load = async () => {
    const r = await api.get("/leads", { params: { q, status } });
    setItems(r.data);
  };
  useEffect(() => { load(); }, [q, status]);

  // Fetch dropdown options whenever dialog opens so Settings changes are live
  useEffect(() => {
    if (!open) return;
    api.get("/settings/stages").then(r => setStages(r.data));
    api.get("/settings/sources").then(r => setSources(r.data));
    api.get("/settings/industries").then(r => setIndustries(r.data));
  }, [open]);

  const openCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (l) => {
    setEditingId(l.id);
    setForm({
      name: l.name || "", company: l.company || "", contact_person: l.contact_person || "",
      phone: l.phone || "", whatsapp: l.whatsapp || "", email: l.email || "",
      lead_source: l.lead_source || "", industry: l.industry || "",
      status: l.status || "new", qualification: l.qualification || "warm",
      potential_value: l.potential_value || 0, product: l.product || "", notes: l.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, potential_value: Number(form.potential_value) };
      if (editingId) {
        await api.patch(`/leads/${editingId}`, payload);
        toast.success("Lead diperbarui");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead ditambahkan & tersinkron ke pipeline");
      }
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch { toast.error("Gagal menyimpan"); }
  };
  const del = async (id) => { if(!window.confirm("Hapus lead & deal terkait?"))return; await api.delete(`/leads/${id}`); load(); };
  const convert = async (id) => { await api.post(`/leads/${id}/convert`); toast.success("Dikonversi ke qualified"); load(); };
  const exportCsv = async () => {
    const ok = await downloadCsv(`/export/leads.csv${status && status!=="all" ? `?status=${status}` : ""}`, `leads-${new Date().toISOString().slice(0,10)}.csv`);
    if (ok) toast.success("Ekspor leads berhasil"); else toast.error("Gagal ekspor");
  };

  return (
    <div className="space-y-5" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{fontFamily:"Outfit"}}>Leads</h1>
          <p className="text-sm text-slate-500">Kelola dan kualifikasi prospek Anda. Lead baru otomatis muncul di Pipeline sesuai statusnya.</p>
        </div>
        <div className="flex gap-2">
        <Button variant="outline" onClick={exportCsv} data-testid="export-leads-btn"><Download className="w-4 h-4 mr-1"/>Export CSV</Button>
        <Button variant="outline" onClick={()=>setImportOpen(true)} data-testid="import-leads-btn"><Upload className="w-4 h-4 mr-1"/>Import CSV/Excel</Button>
        <ImportDialog open={importOpen} onOpenChange={setImportOpen}
          endpoint="/leads/import" templateUrl="/api/templates/leads.csv"
          entityLabel="leads" testIdPrefix="import-leads"
          onDone={load}/>
        <Button className="brand-btn" onClick={openCreate} data-testid="add-lead-btn"><Plus className="w-4 h-4 mr-1"/>Lead Baru</Button>

        <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){ setEditingId(null); }}}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editingId ? "Edit Lead" : "Lead Baru"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2"><Label>Nama Lead</Label><Input data-testid="lead-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></div>
              <div><Label>Perusahaan</Label><Input value={form.company} onChange={(e)=>setForm({...form,company:e.target.value})}/></div>
              <div><Label>Kontak Person</Label><Input value={form.contact_person} onChange={(e)=>setForm({...form,contact_person:e.target.value})}/></div>
              <div><Label>Telepon</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}/></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/></div>
              <div><Label>Sumber</Label>
                <Select value={form.lead_source || undefined} onValueChange={(v)=>setForm({...form,lead_source:v})}>
                  <SelectTrigger data-testid="lead-source"><SelectValue placeholder="Pilih sumber"/></SelectTrigger>
                  <SelectContent>{sources.map(s=><SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Industri</Label>
                <Select value={form.industry || undefined} onValueChange={(v)=>setForm({...form,industry:v})}>
                  <SelectTrigger data-testid="lead-industry"><SelectValue placeholder="Pilih industri"/></SelectTrigger>
                  <SelectContent>{industries.map(i=><SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Kualifikasi</Label>
                <Select value={form.qualification} onValueChange={(v)=>setForm({...form,qualification:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{["hot","warm","cold"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v)=>setForm({...form,status:v})}>
                  <SelectTrigger data-testid="lead-status"><SelectValue/></SelectTrigger>
                  <SelectContent>{stages.map(s=><SelectItem key={s.id} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Nilai Potensi (Rp)</Label><Input type="number" value={form.potential_value} onChange={(e)=>setForm({...form,potential_value:e.target.value})}/></div>
              <div className="col-span-2"><Label>Produk / Layanan</Label><Input value={form.product} onChange={(e)=>setForm({...form,product:e.target.value})}/></div>
              <div className="col-span-2"><Label>Catatan</Label><Input value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})}/></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button className="brand-btn" onClick={save} data-testid="lead-save">{editingId ? "Simpan Perubahan" : "Simpan"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cari lead..." value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9" data-testid="leads-search"/>
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="leads-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              {Object.keys(STAGE_META).map(s=><SelectItem key={s} value={s}>{STAGE_META[s].id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase border-b">
              <tr><th className="text-left py-2 px-2">Lead</th><th className="text-left">Perusahaan</th><th className="text-left">Sumber</th><th>Status</th><th>Kualifikasi</th><th className="text-right">Nilai</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((l)=>(
                <tr key={l.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                  <td className="py-2 px-2 font-medium text-slate-800">{l.name}</td>
                  <td>{l.company}</td>
                  <td className="text-slate-600">{l.lead_source}</td>
                  <td className="text-center"><span className={`badge-status ${stageStyle(l.status)}`}>{stageLabel(l.status)}</span></td>
                  <td className="text-center"><span className={`badge-status ${QUAL_META[l.qualification]?.cls || ""}`}>{QUAL_META[l.qualification]?.id || l.qualification}</span></td>
                  <td className="text-right font-semibold">{formatRupiah(l.potential_value)}</td>
                  <td className="text-right pr-2 whitespace-nowrap space-x-2">
                    <button onClick={()=>openEdit(l)} className="text-slate-500 hover:text-emerald-700" title="Edit lead" data-testid={`edit-lead-${l.id}`}><Pencil className="w-4 h-4 inline"/></button>
                    <button onClick={()=>convert(l.id)} className="text-emerald-700 hover:text-emerald-900" title="Konversi ke Qualified" data-testid={`convert-lead-${l.id}`}><GitBranch className="w-4 h-4 inline"/></button>
                    <button onClick={()=>del(l.id)} className="text-rose-500 hover:text-rose-700" title="Hapus" data-testid={`del-lead-${l.id}`}><Trash2 className="w-4 h-4 inline"/></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500">Belum ada lead.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
